import React, { useState,  useMemo, useCallback } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Button,
  TextField,
  Box,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
} from '@mui/material';
import { getCustomer, postUpdateCustomer } from '../api/api';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';

const Musteriler = () => {
  const [customers, setCustomers] = useState([]);
  const [searchType, setSearchType] = useState('emailAddress'); // emailAddress, governmentId, mobilePhone
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    individualCustomerId: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    governmentId: '',
    licenseDate: '',
    birthDate: '',
    citizenShipId: 'c2e4665c-9aaf-e811-9410-000c293d97f8',
    mobilePhone: '',
    allowMarketingPermissions: true,
  });


    const formatDate = useCallback((dateString) => {
          return new Date(dateString).toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit' 
          });
      }, []);

    const convertDateToInputFormat = useCallback((dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        // HTML date input requires YYYY-MM-DD format
        return `${year}-${month}-${day}`;
      } catch (error) {
        return '';
      }
    }, []);

    const convertDateToServerFormat = useCallback((dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        return '';
      }
    }, []);

  const searchCustomer = async () => {
    if (!searchValue.trim()) {
      toast.warning('Lütfen bir değer girin');
      return;
    }

    setLoading(true);
    try {
      const searchParam = {
        [searchType]: searchValue.trim(),
      };

      const response = await getCustomer(searchParam);

      if (response.isSuccess && response.value) {
        // Ensure response.value is an array
        const customerList = Array.isArray(response.value)
          ? response.value
          : [response.value];

        const customersWithId = customerList.map((item, index) => ({
          ...item,
          id: item.customerId || item.individualCustomerId || index,
          individualCustomerId: item.customerId || item.individualCustomerId || '',
        }));

        setCustomers(customersWithId);

        if (customersWithId.length === 0) {
          toast.info('Müşteri bulunamadı');
        } else {
          toast.success(`${customersWithId.length} müşteri bulundu`);
        }
      } else {
        toast.error('Müşteri araması başarısız');
        setCustomers([]);
      }
    } catch (error) {
      toast.error('Bir hata oluştu: ' + error.message);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      individualAddressInformation: customer.individualAddressInformation || '',
      individualCustomerId: customer.individualCustomerId || '',
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      emailAddress: customer.emailAddress || '',
      governmentId: customer.governmentId || '',
      licenseClass: customer.licenseClass || '',
      genderCode: customer.genderCode || '',
      licenseDate: convertDateToInputFormat(customer.licenseDate) || '',
      birthDate: convertDateToInputFormat(customer.birthDate) || '',
         citizenShipId: 'c2e4665c-9aaf-e811-9410-000c293d97f8',
      mobilePhone: customer.mobilePhone || '',
      allowMarketingPermissions: customer.allowMarketingPermissions ?? true,
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveCustomer = async () => {
    try {
      setLoading(true);
      
      const dataToSend = {
        ...editFormData,
        licenseDate: convertDateToServerFormat(editFormData.licenseDate),
        birthDate: convertDateToServerFormat(editFormData.birthDate),
      };

      const response = await postUpdateCustomer(dataToSend);

      if (response.isSuccess) {
        toast.success('Müşteri bilgileri başarıyla güncellendi');
        setEditDialogOpen(false);

        // Update the customer in the list
        setCustomers((prev) =>
          prev.map((cust) =>
            cust.id === selectedCustomer.id
              ? { ...editFormData, id: editFormData.individualCustomerId }
              : cust
          )
        );
      } else {
        toast.error('Güncelleme başarısız: ' + (response.message || ''));
      }
    } catch (error) {
      toast.error('Bir hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCustomer(null);
  };

  const columns = [
    {
      field: 'actions',
      headerName: 'İşlemler',
      type: 'actions',
      width: 80,
      sortable: false,
      filterable: false,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Düzenle"
          onClick={() => handleEditClick(params.row)}
        />,
      ],
    },
    {
      field: 'firstName',
      headerName: 'Adı',
      minWidth: 120,
    },
    {
      field: 'lastName',
      headerName: 'Soyadı',
     
      minWidth: 120,
    },
    {
      field: 'emailAddress',
      headerName: 'E-Posta',
     
      minWidth: 180,
    },
    {
      field: 'governmentId',
      headerName: 'T.C. Kimlik No',
      width: 140,
    },
    {
      field: 'mobilePhone',
      headerName: 'Telefon',
      width: 140,
    },
    {
      field: 'birthDate',
      headerName: 'Doğum Tarihi',
      width: 130,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'licenseDate',
      headerName: 'Ehliyet Tarihi',
      width: 130,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
  
    {
      field: 'allowMarketingPermissions',
      headerName: 'Pazarlama İzni',
      width: 130,
      renderCell: (params) => (params.value ? 'Evet' : 'Hayır'),
    },
  ];

  return (
    <Card sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Arama Türünü Seçin:
          </Typography>
          <RadioGroup
            row
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
          >
            <FormControlLabel
              value="emailAddress"
              control={<Radio />}
              label="E-Posta "
            />
            <FormControlLabel
              value="governmentId"
              control={<Radio />}
              label="T.C. Kimlik No"
            />
            <FormControlLabel
              value="mobilePhone"
              control={<Radio />}
              label="Telefon No"
            />
          </RadioGroup>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4} sm={4}>
            <TextField
              placeholder="Arama değerini girin..."
              variant="outlined"
              fullWidth
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchCustomer();
                }
              }}
            />
          <br></br> <br></br>
            <Button
              variant="contained"
              color="primary"
              onClick={searchCustomer}
              disabled={loading}
              fullWidth
              sx={{ height: '40px' }}
            >
              {loading ? 'Aranıyor...' : 'Ara'}
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
          Bulunan Müşteri Sayısı: {customers.length}
        </Box>
      </Box>

      <Box sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={customers}
          columns={columns}
          pageSizeOptions={[]}
          disablePaginationControl
          hideFooterPagination
          autoHeight
          onRowDoubleClick={(params) => handleEditClick(params.row)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            cursor: 'pointer',
          }}
        />
      </Box>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Müşteri Bilgilerini Düzenle</DialogTitle>
        <DialogContent sx={{ pt: 6 }}>
          <br></br>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adı"
                name="firstName"
                value={editFormData.firstName}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Soyadı"
                name="lastName"
                value={editFormData.lastName}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="E-Posta"
                name="emailAddress"
                type="email"
                value={editFormData.emailAddress}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="T.C. Kimlik No"
                name="governmentId"
                value={editFormData.governmentId}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefon"
                name="mobilePhone"
                value={editFormData.mobilePhone}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Doğum Tarihi"
                name="birthDate"
                type="date"
                value={editFormData.birthDate}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ehliyet Tarihi"
                name="licenseDate"
                type="date"
                value={editFormData.licenseDate}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                label="Vatandaşlık ID"
                name="citizenShipId"
                value={editFormData.citizenShipId}
                onChange={handleEditFormChange}
                fullWidth
                size="small"
              />
            </Grid> */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="allowMarketingPermissions"
                    checked={editFormData.allowMarketingPermissions}
                    onChange={handleEditFormChange}
                  />
                }
                label="Pazarlama Ayarlarına İzin Ver"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>İptal</Button>
          <Button
            onClick={handleSaveCustomer}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default Musteriler;
